import React, { useEffect, useState } from 'react';
import { Check, ChevronDown, ChevronUp, CircleAlert, X } from 'lucide-react';
import { useDrive } from '../../contexts/DriveProvider';
import { formatSize } from '../../lib/files';

const UploadPanel = () => {
  const { uploads, cancelUpload, clearUploads } = useDrive();
  const [collapsed, setCollapsed] = useState(false);
  const running = uploads.filter((u) => u.status === 'uploading' || u.status === 'queued');
  const failed = uploads.filter((u) => u.status === 'error');

  // Get out of the way once everything uploaded fine; failures stay until dismissed
  const settled = uploads.length > 0 && running.length === 0 && failed.length === 0;
  useEffect(() => {
    if (!settled) return undefined;
    const t = setTimeout(clearUploads, 4000);
    return () => clearTimeout(t);
  }, [settled, clearUploads]);

  if (!uploads.length) return null;
  const title = running.length
    ? `Uploading ${running.length} item${running.length > 1 ? 's' : ''}`
    : failed.length
      ? `${failed.length} upload${failed.length > 1 ? 's' : ''} failed`
      : 'Uploads complete';

  return (
    <section
      aria-label="Uploads"
      className="fixed bottom-4 right-4 z-40 w-[min(380px,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
    >
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <span className="flex-1 font-semibold">{title}</span>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? 'Expand uploads' : 'Collapse uploads'}
          className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {collapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {!running.length && (
          <button
            type="button"
            onClick={clearUploads}
            aria-label="Close uploads"
            className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {!collapsed && (
        <ul className="max-h-72 overflow-y-auto py-1">
          {uploads.map((u) => (
            <li key={u.id} className="flex items-center gap-3 px-4 py-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm">{u.name}</div>
                {u.status === 'uploading' || u.status === 'queued' ? (
                  <div className="mt-1.5 h-1.5 rounded-full bg-muted">
                    <div className="h-1.5 rounded-full bg-primary transition-[width]" style={{ width: `${Math.round(u.progress * 100)}%` }} />
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">
                    {u.status === 'error' ? u.error : u.status === 'cancelled' ? 'Cancelled' : formatSize(u.size)}
                  </div>
                )}
              </div>
              {u.status === 'done' && <Check className="h-5 w-5 text-external" aria-label="Uploaded" />}
              {u.status === 'error' && <CircleAlert className="h-5 w-5 text-destructive" aria-label="Failed" />}
              {(u.status === 'uploading' || u.status === 'queued') && (
                <button
                  type="button"
                  onClick={() => cancelUpload(u.id)}
                  aria-label={`Cancel ${u.name}`}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default UploadPanel;
